import { supabase } from '@/lib/supabase';
import { Lang } from '@/data/constants';
import { getAttribution, getVisitorId, track } from '@/lib/tracking';

/**
 * SINGLE SOURCE OF TRUTH for sending ANY form submission in the platform to
 * the CRM / GoHighLevel. Every form (registration, contact, quote request,
 * lead request, contractor & subcontractor applications, homeowner forms,
 * support, checkout, future lead-capture forms) must call submitForm().
 *
 * Since the security hardening pass, submitForm() posts to the PROTECTED
 * `crm-capture` edge function, which (server-side):
 *   - rate limits by IP / visitor / email / phone,
 *   - verifies the Cloudflare Turnstile token,
 *   - validates the payload against allow-lists,
 *   - records the consent snapshot,
 *   - upserts the GoHighLevel contact (dedupe on email, then phone),
 *   - queues failures in mkt_crm_queue for automatic retry.
 *
 * GoHighLevel credentials are never exposed to the browser.
 */

export type FormType =
  | 'register-homeowner'
  | 'register-gc'
  | 'register-subcontractor'
  | 'registration-started'
  | 'contact'
  | 'support'
  | 'quote-request'
  | 'lead-request'
  | 'callback-request'
  | 'waitlist'
  | 'premium-setup'
  | 'project-posted'
  | 'work-order-posted'
  | 'lead-purchase'
  | 'subscription'
  | 'checkout-started'
  | 'newsletter'
  // SMS re-opt-in (legacy-consent email) and the PRIMARY A2P opt-in page
  | 'sms-reoptin'
  | 'sms-opt-in'
  // ---- behavioural activity events (see crmActivity) --------------------
  | 'lead-viewed'
  | 'lead-purchased'
  | 'registration-completed'
  | 'email-verified'
  | 'profile-updated';

/** Form type -> GoHighLevel pipeline stage name (create these stages in GHL). */
export const GHL_PIPELINE_STAGE: Record<FormType, string> = {
  'register-homeowner': 'New Registration',
  'register-gc': 'New Registration',
  'register-subcontractor': 'New Registration',
  'registration-started': 'Registration Started',
  contact: 'New Inquiry',
  support: 'Support Request',
  'quote-request': 'New Inquiry',
  'lead-request': 'New Inquiry',
  'callback-request': 'Support Request',
  waitlist: 'New Inquiry',
  'premium-setup': 'Subscriber',
  'project-posted': 'New Project',
  'work-order-posted': 'Work Order Created',
  'lead-purchase': 'Lead Purchased',
  subscription: 'Subscriber',
  'checkout-started': 'Checkout Started',
  newsletter: 'New Inquiry',
  'sms-reoptin': 'Engaged',
  'sms-opt-in': 'Engaged',
  'lead-viewed': 'Engaged',
  'lead-purchased': 'Lead Purchased',
  'registration-completed': 'Registered',
  'email-verified': 'Registered',
  'profile-updated': 'Registered',
};

/**
 * Action -> GoHighLevel tags. Mirrors the TAG_MAP inside the crm-capture function.
 *
 * BRANDING EXCEPTION — READ BEFORE "FIXING" THE CASING HERE.
 * These strings are NOT display copy. They are the tag IDENTIFIERS that already
 * exist on live GoHighLevel contacts and that the deployed `crm-capture` edge
 * function matches on byte-for-byte. Re-casing them to GIGGONE would create a
 * second, parallel set of tags in the CRM and orphan the segmentation on every
 * contact captured so far, while changing nothing a customer can see. They stay
 * as-is on purpose. Anything a human actually reads — sender names, email
 * bodies, SMS/WhatsApp copy, admin labels — goes through BRAND / brandCase()
 * from src/lib/brand.ts and renders as GIGGONE.
 */
export const GHL_TAGS: Record<FormType, string[]> = {

  'register-homeowner': ['GigGone Lead', 'GigGone Customer', 'GigGone Homeowner', 'GigGone Registered'],
  'register-gc': ['GigGone Lead', 'GigGone Contractor', 'GigGone Registered'],
  'register-subcontractor': ['GigGone Lead', 'GigGone Contractor', 'GigGone Subcontractor', 'GigGone Registered'],
  'registration-started': ['GigGone Lead', 'GigGone Registration Started', 'GigGone Unverified'],
  contact: ['GigGone Lead', 'GigGone Contact Request'],
  support: ['GigGone Lead', 'GigGone Contact Request'],
  'quote-request': ['GigGone Lead', 'GigGone Customer'],
  'lead-request': ['GigGone Lead', 'GigGone Contractor'],
  'callback-request': ['GigGone Lead', 'GigGone Contact Request'],
  waitlist: ['GigGone Lead'],
  'premium-setup': ['GigGone Lead', 'GigGone Premium'],
  'project-posted': ['GigGone Lead', 'GigGone Customer', 'GigGone Lead Seller', 'GigGone Posted Project'],
  'work-order-posted': ['GigGone Lead', 'GigGone Contractor', 'GigGone Lead Seller'],
  'lead-purchase': ['GigGone Customer', 'GigGone Paid', 'GigGone Lead Buyer'],
  subscription: ['GigGone Customer', 'GigGone Paid'],
  'checkout-started': ['GigGone Lead', 'GigGone Checkout Started'],
  newsletter: ['GigGone Lead', 'GigGone Newsletter'],
  'sms-reoptin': ['GigGone SMS Re-Opt-In'],
  'sms-opt-in': ['GigGone SMS Opt-In Page'],
  'lead-viewed': ['GigGone Contractor', 'GigGone Viewed Lead'],
  'lead-purchased': ['GigGone Customer', 'GigGone Paid', 'GigGone Lead Buyer', 'GigGone Purchased Lead'],
  'registration-completed': ['GigGone Registered'],
  'email-verified': ['GigGone Registered', 'GigGone Email Verified'],
  'profile-updated': ['GigGone Registered'],
};

/**
 * The audience a contact belongs to. Drives the onboarding nurture sequence
 * that GoHighLevel runs (contractor / subcontractor / homeowner onboarding).
 */
export type Audience = 'homeowner' | 'gc' | 'subcontractor' | 'visitor';

/** Audience -> the GHL user_type value + the newsletter form's intent tags. */
export const AUDIENCE_META: Record<Audience, { userType: string; tags: string[]; nurture: string }> = {
  homeowner: {
    userType: 'homeowner',
    tags: ['GigGone Homeowner', 'audience:homeowner'],
    nurture: 'homeowner-onboarding',
  },
  gc: {
    userType: 'gc',
    tags: ['GigGone Contractor', 'audience:gc'],
    nurture: 'contractor-onboarding',
  },
  subcontractor: {
    userType: 'subcontractor',
    tags: ['GigGone Subcontractor', 'audience:subcontractor'],
    nurture: 'subcontractor-onboarding',
  },
  visitor: {
    userType: 'lead',
    tags: ['GigGone Visitor', 'audience:visitor'],
    nurture: 'visitor-nurture',
  },
};


export interface FormSubmission {
  formType: FormType;
  email: string;
  name?: string;
  phone?: string;
  language: Lang;
  userType?: string;
  companyName?: string;
  plan?: string;
  userId?: string;
  /**
   * SMS consent. TRUE only when the visitor ticked the (unchecked-by-default)
   * SmsConsentCheckbox. Never hard-code true. Use smsConsentPayload() from
   * src/data/smsConsent.ts so the disclosure version + page URL travel with it.
   */
  smsOptIn?: boolean;
  /** Version id of the exact SMS disclosure shown (src/data/smsConsent.ts). */
  smsDisclosureVersion?: string;
  /** Page the consent was captured on (evidence for mkt_consents). */
  pageUrl?: string;
  /** Explicit marketing consent captured on the form (checkbox / stated notice). */
  consent?: boolean;
  /** Cloudflare Turnstile token — verified server-side inside crm-capture. */
  turnstileToken?: string;
  registrationStatus?: string;
  checkoutStatus?: string;
  paymentStatus?: string;
  paid?: boolean;
  paymentFailed?: boolean;
  /** Geography + trade: drive GHL segmentation and the by-trade campaigns. */
  city?: string;
  state?: string;
  postalCode?: string;
  trade?: string;
  tradeSlug?: string;
  tradeLabel?: string;
  /** Behavioural label recorded on the contact (last_activity custom field). */
  activity?: string;
  /** Extra key/value data forwarded to GoHighLevel custom fields + webhook. */
  fields?: Record<string, string | number | boolean | null | undefined>;
  tags?: string[];
  /** Set false for silent syncs that should not trigger a confirmation email. */
  sendEmail?: boolean;
}

export interface CrmResult {
  ok: boolean;
  crm?: string;
  contactId?: string | null;
  queued?: boolean;
  error?: string;
  message?: string;
}

/**
 * Creates/updates the contact in GoHighLevel and fires the matching workflow.
 * Always non-blocking for the visitor: a CRM outage can never break a form —
 * the payload is queued server-side and retried with exponential backoff.
 */
export const submitForm = async (s: FormSubmission): Promise<CrmResult> => {
  const cleanFields = Object.fromEntries(
    Object.entries(s.fields || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

  const attr = getAttribution();
  const [firstName, ...rest] = String(s.name || '').trim().split(/\s+/);

  try {
    const { data, error } = await supabase.functions.invoke('crm-capture', {
      body: {
        formType: s.formType,
        email: s.email,
        name: s.name,
        firstName: firstName || undefined,
        lastName: rest.length ? rest.join(' ') : undefined,
        phone: s.phone || undefined,
        companyName: s.companyName,
        userType: s.userType || 'lead',
        plan: s.plan || '',
        userId: s.userId,
        language: s.language,
        smsOptIn: s.smsOptIn === true,
        smsDisclosureVersion: s.smsOptIn === true ? s.smsDisclosureVersion : undefined,
        pageUrl: s.pageUrl || (typeof window !== 'undefined' ? window.location.href.slice(0, 500) : undefined),
        consent: s.consent !== false,
        turnstileToken: s.turnstileToken,
        registrationStatus: s.registrationStatus,
        checkoutStatus: s.checkoutStatus,
        paymentStatus: s.paymentStatus,
        paid: s.paid === true,
        paymentFailed: s.paymentFailed === true,
        city: s.city,
        state: s.state,
        postalCode: s.postalCode,
        trade: s.trade,
        tradeSlug: s.tradeSlug || s.trade,
        tradeLabel: s.tradeLabel || s.trade,
        activity: s.activity,
        visitorId: getVisitorId(),
        idempotencyKey: `${s.formType}:${(s.email || '').toLowerCase()}:${Date.now().toString(36).slice(0, 7)}`,
        source: s.formType,
        attribution: {
          source: attr.source,
          landingPage: attr.landingPage,
          referrer: attr.referrer,
          utmSource: attr.utmSource,
          utmMedium: attr.utmMedium,
          utmCampaign: attr.utmCampaign,
          utmTerm: attr.utmTerm,
          utmContent: attr.utmContent,
          firstSeen: attr.firstSeen,
          lastSeen: attr.lastSeen,
        },
        tags: [...(GHL_TAGS[s.formType] || []), `stage:${GHL_PIPELINE_STAGE[s.formType]}`, ...(s.tags || [])],
        fields: cleanFields,
      },
    });

    if (error) return { ok: false, error: 'network' };
    track('form_completed', { form: s.formType });
    return (data || { ok: false }) as CrmResult;
  } catch (_e) {
    return { ok: false, error: 'network' };
  }
};

/* ------------------------------------------------------------------------ */
/* ACTIVITY EVENTS                                                           */
/* ------------------------------------------------------------------------ */

export type ActivityType =
  | 'lead-viewed'
  | 'lead-purchased'
  | 'project-posted'
  | 'registration-completed'
  | 'email-verified'
  | 'profile-updated';

/**
 * SINGLE SOURCE OF TRUTH for pushing platform BEHAVIOUR (not form fills) into
 * GoHighLevel: viewed a lead, purchased a lead, posted a project, completed
 * registration, verified email. GHL workflows trigger on the `form:<type>` and
 * `activity:<type>` tags plus the `last_activity` custom field.
 *
 * Fire-and-forget by design — an activity ping must never delay or break the
 * user action that produced it. Callers do NOT need to await this.
 */
export const crmActivity = async (
  activity: ActivityType,
  s: Omit<FormSubmission, 'formType' | 'activity'> & { email: string }
): Promise<CrmResult> => {
  // project-posted already exists as a first-class form type with its own
  // pipeline stage, so it maps straight through.
  const formType: FormType = activity === 'project-posted' ? 'project-posted' : (activity as FormType);
  if (!s.email) return { ok: false, error: 'no_email' };
  try {
    return await submitForm({
      ...s,
      formType,
      activity,
      // Behaviour is not consent. Never let an activity ping fabricate one.
      consent: false,
      tags: [...(s.tags || []), `activity:${activity}`],
    });
  } catch (_e) {
    return { ok: false, error: 'network' };
  }
};

/** Convenience: dedupe repeated activity pings within one browser session. */
const firedThisSession = new Set<string>();
export const crmActivityOnce = (
  activity: ActivityType,
  key: string,
  s: Omit<FormSubmission, 'formType' | 'activity'> & { email: string }
): void => {
  const k = `${activity}:${key}`;
  if (firedThisSession.has(k)) return;
  firedThisSession.add(k);
  void crmActivity(activity, s);
};
